const READ_MORE_TRUNCATION_TOLERANCE = 1.05;
const READ_MORE_GRADIENT_LINES = 2;
const READ_MORE_ID_PREFIX = 'read-more-content-';
const READ_MORE_TRANSITION_MS = 300;
const READ_MORE_TRANSITION_EASING = 'ease-in-out';

class ResponsiveReadMore {
  constructor(selector = '[data-read-more]', debounceDelay = 150) {
    this.selector = selector;
    this.debounceDelay = debounceDelay;
    this.elements = [];
    this.idCounter = 0;
    this.lastWindowWidth = window.innerWidth;
    this.resizeHandler = this.debounce(this.onResize.bind(this), this.debounceDelay);
    this.init();
  }

  init() {
    this.elements = Array.from(document.querySelectorAll(this.selector));
    this.elements.forEach((el) => {
      const config = this.parseConfig(el.dataset.readMore);
      if (!config) return;

      el.__readMoreState = {
        config,
        button: null,
        isExpanded: false,
        transitionEndHandler: null
      };

      this.applyTruncation(el);
    });

    window.addEventListener('resize', this.resizeHandler);
  }

  parseConfig(raw) {
    try {
      const parsed = JSON.parse(raw);
      const { mobile, tablet, desktop } = parsed;
      if ([mobile, tablet, desktop].some((v) => typeof v !== 'number')) return null;
      return { mobile, tablet, desktop };
    } catch (e) {
      console.warn('[ReadMore] Invalid config:', raw);
      return null;
    }
  }

  getBreakpoint() {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  getLineHeight(el) {
    const style = window.getComputedStyle(el);
    let lh = parseFloat(style.lineHeight);
    if (isNaN(lh) || style.lineHeight === 'normal') {
      lh = 1.2 * parseFloat(style.fontSize);
    }
    return lh;
  }

  getEffectiveLineHeight(el) {
    const lineHeight = this.getLineHeight(el);
    const blocks = Array.from(el.querySelectorAll(':scope > p, :scope > div, :scope > li, :scope > ul, :scope > ol'));
    if (!blocks.length) return lineHeight;
    const avgMargin = blocks.reduce((sum, b) => sum + (parseFloat(window.getComputedStyle(b).marginBottom) || 0), 0) / blocks.length;
    return lineHeight + avgMargin;
  }

  getTrailingMargin(el) {
    const lastBlock = el.querySelector('p:last-child, div:last-child, ul:last-child, ol:last-child');
    if (!lastBlock) return 0;
    return parseFloat(window.getComputedStyle(lastBlock).marginBottom) || 0;
  }

  getContentId(el) {
    if (!el.id) {
      this.idCounter += 1;
      el.id = `${READ_MORE_ID_PREFIX}${this.idCounter}`;
    }
    return el.id;
  }

  resetTruncation(el) {
    const state = el.__readMoreState;
    if (!state) return;

    if (state.transitionEndHandler) {
      el.removeEventListener('transitionend', state.transitionEndHandler);
      state.transitionEndHandler = null;
    }

    el.classList.remove('read-more-content');
    el.style.maxHeight = 'none';
    el.style.overflow = 'visible';
    el.style.removeProperty('--collapsed-height');
    el.style.removeProperty('--gradient-height');
    el.style.removeProperty('transition');
    el.removeAttribute('aria-expanded');
    state.isExpanded = false;

    if (state.button) {
      state.button.remove();
      state.button = null;
    }
  }

  applyTruncation(el, keepExpandedState = false) {
    const state = el.__readMoreState;
    if (!state) return;

    const wasExpanded = keepExpandedState && state.isExpanded;
    this.resetTruncation(el);

    const lines = state.config[this.getBreakpoint()];
    const lineHeight = this.getLineHeight(el);
    const effectiveLineHeight = this.getEffectiveLineHeight(el);
    const trailingMargin = this.getTrailingMargin(el);
    const naturalHeight = lines * effectiveLineHeight;
    const shouldTruncate = el.scrollHeight > naturalHeight * READ_MORE_TRUNCATION_TOLERANCE;

    if (!shouldTruncate) return;

    const collapsedHeight = Math.max(naturalHeight - trailingMargin, lineHeight);
    const gradientHeight = (READ_MORE_GRADIENT_LINES * lineHeight) + trailingMargin;

    el.classList.add('read-more-content');
    el.style.setProperty('--collapsed-height', `${collapsedHeight}px`);
    el.style.setProperty('--gradient-height', `${gradientHeight}px`);
    el.style.maxHeight = `${collapsedHeight}px`;
    el.style.overflow = 'hidden';
    el.style.transition = `max-height ${READ_MORE_TRANSITION_MS}ms ${READ_MORE_TRANSITION_EASING}`;
    el.setAttribute('aria-expanded', 'false');

    this.addToggleButton(el, collapsedHeight, wasExpanded);
  }

  addToggleButton(el, collapsedHeight, initiallyExpanded = false) {
    const state = el.__readMoreState;
    if (!state) return;

    if (state.button) {
      state.button.remove();
      state.button = null;
    }

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'read-more-toggle semibold link';
    const readMoreText = window.themeVariables?.strings?.accessibilityReadMore || 'Read more';
    const readLessText = window.themeVariables?.strings?.accessibilityReadLess || 'Read less';
    btn.textContent = readMoreText;
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', this.getContentId(el));

    if (initiallyExpanded) {
      state.isExpanded = true;
      el.style.maxHeight = 'none';
      el.setAttribute('aria-expanded', 'true');
      btn.setAttribute('aria-expanded', 'true');
      btn.textContent = readLessText;
    } else {
      state.isExpanded = false;
    }

    btn.addEventListener('click', () => {
      const nextExpanded = !state.isExpanded;

      if (state.transitionEndHandler) {
        el.removeEventListener('transitionend', state.transitionEndHandler);
        state.transitionEndHandler = null;
      }

      if (nextExpanded) {
        const expandedHeight = el.scrollHeight;
        el.style.maxHeight = `${expandedHeight}px`;

        const onTransitionEnd = (event) => {
          if (event.propertyName !== 'max-height') return;
          if (!state.isExpanded) return;
          el.style.maxHeight = 'none';
          el.removeEventListener('transitionend', onTransitionEnd);
          state.transitionEndHandler = null;
        };

        state.transitionEndHandler = onTransitionEnd;
        el.addEventListener('transitionend', onTransitionEnd);
      } else {
        if (el.style.maxHeight === 'none') {
          el.style.maxHeight = `${el.scrollHeight}px`;
          void el.offsetHeight;
        }
        el.style.maxHeight = `${collapsedHeight}px`;
      }

      state.isExpanded = nextExpanded;
      el.setAttribute('aria-expanded', nextExpanded ? 'true' : 'false');
      btn.setAttribute('aria-expanded', nextExpanded ? 'true' : 'false');
      btn.textContent = nextExpanded ? readLessText : readMoreText;
    });

    el.after(btn);
    state.button = btn;
  }

  onResize() {
    const nextWindowWidth = window.innerWidth;
    if (Math.abs(nextWindowWidth - this.lastWindowWidth) < 2) return;

    this.lastWindowWidth = nextWindowWidth;
    this.elements.forEach((el) => {
      const state = el.__readMoreState;
      if (!state) return;
      this.applyTruncation(el, true);
    });
  }

  debounce(fn, delay) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }
}

window.addEventListener('DOMContentLoaded', () => {
  new ResponsiveReadMore();
});
